import Image from "next/image";

import cn from "classnames";

import {
  type FrontendIconName,
  getFrontendIconPreview,
} from "@/components/icons/iconRegistry";
import { Text } from "@/components/text/text.component";

import type { StepsSectionStepProps } from "./stepsSection.types";

const IMAGE_SIZES = "(max-width: 1023px) 80vw, 33vw";

export function StepsSectionStep({
  title,
  description,
  media,
  index,
  showIndicator,
  showConnector,
}: Omit<StepsSectionStepProps, "key"> & {
  index: number;
  showIndicator: boolean;
  showConnector: boolean;
}) {
  return (
    <div className="flex h-full flex-col items-center gap-6 text-center">
      {media?.kind === "image" ? (
        <div className="w-full overflow-hidden rounded-2xl">
          {media.mobile ? (
            <Image
              src={media.mobile.src}
              alt={media.mobile.alt || media.alt}
              width={media.mobile.width}
              height={media.mobile.height}
              sizes={IMAGE_SIZES}
              className="h-auto w-full lg:hidden"
            />
          ) : null}
          <Image
            src={media.src}
            alt={media.alt}
            width={media.width}
            height={media.height}
            sizes={IMAGE_SIZES}
            className={cn("h-auto w-full", media.mobile && "hidden lg:block")}
          />
        </div>
      ) : null}
      {media?.kind === "icon" ? <StepIcon icon={media.icon} /> : null}

      {showIndicator ? (
        <div className="relative flex w-full justify-center">
          {showConnector ? (
            <span
              aria-hidden="true"
              className="border-secondary-100 absolute top-8.75 right-[calc(-50%+0.6875rem)] left-[calc(50%+2.6875rem)] hidden -translate-y-1/2 border-t-2 border-dashed lg:block"
            />
          ) : null}
          <span
            aria-hidden="true"
            className="border-secondary text-secondary-100 relative flex size-10 items-center justify-center rounded-full border bg-white text-base leading-none font-bold lg:size-17.5 lg:text-3xl">
            {index + 1}
          </span>
        </div>
      ) : null}

      <div className="flex flex-col gap-2">
        <Text
          variant="h6"
          component="h3"
          className="text-secondary max-sm:text-2xl/[1.15]">
          {title}
        </Text>
        {description}
      </div>
    </div>
  );
}

function StepIcon({ icon }: { icon: FrontendIconName }) {
  const Icon = getFrontendIconPreview(icon);
  if (!Icon) return null;
  return (
    <Icon
      width={48}
      height={48}
      className="text-secondary"
      aria-hidden="true"
    />
  );
}
